<template>
  <a-modal :open="queue.length > 0" :closable="false" :mask-closable="false" :width="'66vw'" :footer="null" :body-style="{ padding: 0 }">
    <template #title>
      <div style="display: flex; align-items: center; gap: 8px">
        <a-tag :color="typeColor" style="margin: 0">{{ t(`broadcast.form.type_${broadcastType}`) }}</a-tag>
        <span>{{ currentBroadcast?.title }}</span>
      </div>
    </template>
    <div v-if="currentBroadcast" class="broadcast-modal-body">
      <div class="broadcast-content" v-html="renderedMessage" />
      <div class="broadcast-footer">
        <a-divider style="margin: 0 0 12px" />
        <div style="display: flex; flex-direction: column; align-items: center; gap: 8px">
          <a-button type="primary" :loading="dismissing" @click="handleConfirm">
            {{ t("broadcast.modal.confirm") }}
          </a-button>
          <span v-if="queue.length > 1" style="color: #888; font-size: 12px">
            {{ t("broadcast.modal.counter", { current: 1, total: queue.length }) }}
          </span>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { marked } from "marked";
import { sanitizeMarkdown } from "../utils/sanitizer";
import { getActiveBroadcasts, dismissBroadcast } from "../api/broadcast";
import { useUserStore } from "../store/modules/user";
import { Broadcast } from "../models/broadcast";

export default defineComponent({
  name: "BroadcastModal",
  setup() {
    const { t } = useI18n();
    const store = useUserStore();
    const queue = ref<Broadcast[]>([]);
    const dismissing = ref(false);

    const currentBroadcast = computed(() => queue.value[0] ?? null);

    const broadcastType = computed(() => currentBroadcast.value?.type || "info");

    const typeColor = computed(() => {
      const colors: Record<string, string> = { info: "blue", maintenance: "gold", warning: "orange", error: "red" };
      return colors[broadcastType.value] ?? "blue";
    });

    const renderedMessage = computed(() => {
      if (!currentBroadcast.value) return "";
      const html = marked.parse(currentBroadcast.value.message) as string;
      return sanitizeMarkdown(html);
    });

    const SEVERITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

    const fetchBroadcasts = async () => {
      try {
        const broadcasts = await getActiveBroadcasts();
        queue.value = broadcasts.sort((a, b) => {
          const aRank = SEVERITY_ORDER[a.severity ?? "low"] ?? 2;
          const bRank = SEVERITY_ORDER[b.severity ?? "low"] ?? 2;
          return aRank - bRank;
        });
      } catch {
        // error already shown by api module
      }
    };

    watch(
      () => store.isAuthenticated,
      (authenticated) => {
        if (authenticated) {
          fetchBroadcasts();
        } else {
          queue.value = [];
        }
      },
      { immediate: true }
    );

    const handleConfirm = async () => {
      if (!currentBroadcast.value) return;
      dismissing.value = true;
      try {
        await dismissBroadcast(currentBroadcast.value.id);
        queue.value.shift();
      } catch {
        // error already shown by api module
      } finally {
        dismissing.value = false;
      }
    };

    return {
      t,
      queue,
      currentBroadcast,
      broadcastType,
      typeColor,
      renderedMessage,
      dismissing,
      handleConfirm,
    };
  },
});
</script>

<style scoped>
.broadcast-modal-body {
  display: flex;
  flex-direction: column;
  max-height: 75vh;
  min-height: 300px;
  padding: 24px 24px 0;
}

.broadcast-content {
  flex: 1;
  overflow-y: auto;
  line-height: 1.7;
  padding-right: 6px;
  scrollbar-width: thin;
}

.broadcast-footer {
  flex-shrink: 0;
  padding-bottom: 16px;
}

.broadcast-content :deep(h1),
.broadcast-content :deep(h2),
.broadcast-content :deep(h3) {
  margin-top: 16px;
  margin-bottom: 8px;
}

.broadcast-content :deep(ul),
.broadcast-content :deep(ol) {
  padding-left: 24px;
}

.broadcast-content :deep(a) {
  word-break: break-word;
}

.broadcast-content :deep(code) {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.broadcast-content :deep(pre) {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
}
</style>
